import type { Step } from "react-joyride";

/**
 * Guided tour steps for Account Settings → Roles & Permissions.
 *
 * Targets stable `data-testid`s already rendered on the screen (the roles list,
 * the Create Role button, the colour legend, the matrix sheet and the save
 * button), so the tour works regardless of which roles exist. Joyride auto-skips
 * any target not currently in the DOM.
 */
export const rolesPermissionsSteps: Step[] = [
	{
		target: '[data-testid="roles-list"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Your roles</h3>
				<p>
					These are your company&apos;s roles. <strong>System roles</strong> (badged) ship
					with the platform and are read-only. Click any role to focus it — the matrix on
					the right collapses to just that role so it&apos;s easy to read.
				</p>
			</div>
		),
		placement: "right",
		disableBeacon: true,
	},
	{
		target: '[data-testid="create-role-button"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Create a role</h3>
				<p>
					Make your own role — either as a copy of a system role (a good starting point)
					or from scratch. You can then tailor its permissions in the matrix.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="matrix-legend"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Reading the matrix</h3>
				<p>
					Each cell is colour-coded by access level — <strong>Full</strong>,{" "}
					<strong>View</strong>, <strong>Comment</strong>, <strong>Event only</strong> or{" "}
					<strong>No access</strong>. Striped cells aren&apos;t in your subscription tier.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="matrix-sheet-wrap"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Set permissions</h3>
				<p>
					For a role you can edit, click a cell to cycle its access level for that module.
					System roles are locked. A blue dot on a column means it has unsaved changes.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
	{
		target: '[data-testid="save-matrix-changes"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Save your changes</h3>
				<p>
					When you&apos;re happy, click <strong>Save changes</strong>. Levels above your
					subscription are automatically reduced and we&apos;ll let you know if so.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];
